import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance.js";
import Pagination from "../../common/Pagination/index.js";
import Loader from "../../common/Loader/index.js";
import { BiSolidEditAlt } from "react-icons/bi";
import { MdClose, MdDeleteForever } from "react-icons/md";
import Model from "../../common/Model/index.js";
import FormFields from "../../common/FormFields/index.js";
import { notify, Toast } from "../../common/Toast/index.js";
import { getUserMenuPermission } from "../../common/Helper/index.js";
import { LiaSearchPlusSolid } from "react-icons/lia";

const UserMaster = () => {
  const permission = getUserMenuPermission(11);
  const [isUserData, setIsUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    userName: "",
    encryptPassword: "",
    mobile_Number: "",
  });
  const userId = sessionStorage.getItem("userId");

  const fields = [
    {
      name: "userName",
      label: "Username",
      type: "text",
      required: true,
      errorMessage: "User Name is required",
    },
    {
      name: "encryptPassword",
      label: "Password",
      type: "password",
      // required: true,
    },
    {
      name: "mobile_Number",
      label: "Mobile Number",
      type: "number",
      required: true,
      pattern: /^\d{10}$/,
      errorMessage: "10-digit contact number required",
    },
  ];

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `User/GetAllUser?pageNumber=${currentPage}&pageSize=${pageSize}`,
        {
          pageNumber: currentPage,
          pageSize,
        }
      );
      const transformedData = res.data.users.map((user) => ({
        id: user.id,
        userName: user.userName,
        encryptPassword: "",
        mobile_Number: user.mobile_Number,
      }));

      setIsUserData(transformedData);
      setFilteredData(res.data.users);
      setTotalRecords(res.data.totalRecords);
      setTotalPages(
        res.data.users?.length < pageSize ? currentPage : currentPage + 1
      );
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [currentPage]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddUser = () => {
    if (!permission.isInsert) {
      notify("You don't have permission to add User.", "error");
      return;
    }
    setFormData({
      id: "",
      userName: "",
      encryptPassword: "",
      mobile_Number: "",
    });
    setIsEditing(false);
    setIsModelOpen(true);
  };

  const handleEditUser = async (id) => {
    try {
      const { data } = await axiosInstance.get(`User/GetUserById/${id}`);
      setFormData({
        id: data.id || "",
        userName: data.userName || "",
        encryptPassword: "",
        mobile_Number: data.mobile_Number || "",
      });

      setIsEditing(true);
      setTimeout(() => setIsModelOpen(true), 100);
    } catch (err) {
      console.error("Error fetching user data:", err);
    }
  };

  const handleDeleteUser = (id) => {
    setSelectedUserId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`User/DeleteUser/${selectedUserId}`);
      fetchUserData();
      notify("User deleted successfully!", "error");
    } catch (err) {
      notify("Failed to delete User!", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedUserId(null);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !permission.isInsert) return;
    if (isEditing && !permission.isUpdate) return;

    try {
      if (!formData.userName) {
        alert("Username is required");
        return;
      }

      if (!isEditing && !formData.encryptPassword) {
        alert("Password is required for new users.");
        return;
      }

      const payload = isEditing
        ? {
            id: formData.id,
            userName: formData.userName,
            mobile_Number: formData.mobile_Number,
          }
        : {
            userName: formData.userName,
            encryptPassword: formData.encryptPassword,
            mobile_Number: formData.mobile_Number,
          };

      await axiosInstance.post("User/InsertUpdateUser", payload);
      fetchUserData();
      setIsModelOpen(false);
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };
  const fetchFilteredUser = async (query) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `User/FilterByName?userName=${query}`
      );

      const transformedData = res.data.map((user) => ({
        id: user.sale_Person_Id,
        userName: user.Sale_Person_Name,
        encryptPassword: "",
        mobile_Number: user.mobile_Number,
      }));

      setFilteredData(transformedData);
      setTotalRecords(res.data.length);
    } catch (err) {
      notify("Error fetching filtered data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      fetchUserData();
    } else {
      fetchFilteredUser(searchQuery);
    }
  }, [searchQuery, currentPage]);

  const clearSearch = () => {
    setSearchQuery("");
    fetchUserData();
  };
  return (
    <>
      <Toast />

      <div className="rounded-md bg-white shadow-md border px-5 pt-6 pb-2.5">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div className="relative w-full sm:w-1/2 lg:w-1/3">
            <input
              type="text"
              className="w-full bg-gray-100 border border-gray-3 py-2 pl-10 pr-10 rounded-md focus:ring-1 focus:ring-primary focus:border-primary outline-none"
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary">
              <LiaSearchPlusSolid className="w-5 h-5" />
            </button>
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-danger"
              >
                <MdClose className="w-5 h-5" />
              </button>
            )}
          </div>
          {permission.isInsert && (
            <button
              className="bg-primary rounded px-4 text-white py-2 w-full sm:w-auto"
              onClick={handleAddUser}
            >
              + Add User
            </button>
          )}
        </div>
        {loading ? (
          <Loader />
        ) : permission.isView ? (
          <div className="overflow-x-auto  rounded-lg">
            <table className="w-full min-w-[700px] text-sm text-left border border-gray">
              <thead className="bg-primary text-white tracking-widest text-center font-light">
                <tr>
                  <th className="border border-gray px-4 py-3">User Id</th>
                  <th className="border border-gray px-4 py-3">Username</th>
                  <th className="border border-gray px-4 py-3">
                    Mobile Number
                  </th>
                  <th
                    className={`${
                      !permission.isUpdate && !permission.isDelete
                        ? "hidden"
                        : ""
                    } border border-gray px-4 py-3 whitespace-nowrap`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b hover:bg-gray-50 transition cursor-pointer text-center"
                    >
                      <td className="border border-gray-100 px-4 py-2">
                        {user.id}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {user.userName}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {user.mobile_Number}
                      </td>
                      <td
                        className={`border border-gray-100 py-2 text-center ${
                          !permission.isUpdate && !permission.isDelete
                            ? "hidden"
                            : ""
                        }`}
                      >
                        {" "}
                        {permission.isUpdate && (
                          <button
                            className="p-1 text-primary hover:bg-gray-100 rounded-md"
                            onClick={() => handleEditUser(user.id)}
                          >
                            <BiSolidEditAlt className="w-8 h-8 bg-[#e3f2fd] hover:bg-blue-200 p-1 rounded-md outline-none focus:ring-2 focus:ring-red-500" />
                          </button>
                        )}{" "}
                        {permission.isDelete && (
                          <button
                            className="p-1 ml-2 text-danger hover:bg-gray-100 rounded-md"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            <MdDeleteForever className="w-8 h-8 bg-red-100 p-1 rounded-md hover:bg-red-200 outline-none focus:ring-2 focus:ring-red-500" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-red-500 text-lg py-10">
            You don't have permission to view this page.
          </div>
        )}

        <div className="flex justify-between items-center mt-4">
          <p className="text-sm text-gray-6">
            Showing {isUserData?.length + (currentPage - 1) * 10} of{" "}
            {totalRecords} records
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit User" : "Add User"}
      >
        <FormFields
          onSubmit={handleSubmit}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          onClear={() => setIsModelOpen(false)}
          isEditing={isEditing}
        />
      </Model>
      <Model
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-4 text-center">
          <p className="text-lg font-semibold mb-4">
            Are you sure you want to delete this site?
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="bg-gray-5 px-4 py-2 rounded-md"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="bg-danger text-white px-4 py-2 rounded-md"
              onClick={handleConfirmDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default UserMaster;
